import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/system';
import {
  Phone,
  Video,
  EllipsisVertical,
  CheckCheck,
  Paperclip,
  Smile,
  SendHorizontal,
  MessageCircleQuestion,
  FileText
} from 'lucide-react';
import {
  Card,
  Body,
  Avatar,
  OptionList,
  OptionFeedback,
  ReplyOptions,
  shuffledOrder,
  seedFrom
} from '../components/ui';
import {
  motion,
  fadeUp,
  fadeIn,
  bubbleIn,
  popIn,
  typingDot,
  prefersReducedMotion
} from '../components/motion';
import { optionResult } from '../engine/scoring';
import { FORMAT_ICONS } from '../formats';
import CallStep from './CallStep';
import { L, t } from '../strings';

// Shared by `chat` (a messenger thread) and `choice` (a situation in prose).
// A chat step with `channel: 'call'` plays as a phone call instead.
export default function ChatStep(props) {
  if (props.step.type !== 'chat') return <Situation {...props} />;
  return props.step.channel === 'call' ? <CallStep {...props} /> : <Messenger {...props} />;
}

// "09:05" + n minutes, so a thread reads like it happened over a few minutes.
const addMinutes = (at, n) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(at || '');
  if (!m) return at;
  const total = Number(m[1]) * 60 + Number(m[2]) + n;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const REACTION = { best: '👍', ok: '🤔', bad: '😬' };

/* ---------- messenger chrome ---------- */

const Window = styled(Card)(({ theme }) => ({
  padding: 0,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: { padding: 0 }
}));

const Head = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 20px',
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  backgroundColor: '#fff',
  [theme.breakpoints.down('sm')]: { padding: '12px 14px' }
}));

const Stack = styled('div')(() => ({
  display: 'flex',
  '& > *:not(:first-of-type)': { marginLeft: -12 }
}));

const HeadText = styled('div')(() => ({ flex: 1, minWidth: 0 }));

const HeadTitle = styled('div')(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));

const HeadSub = styled('div')(({ theme, active }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  lineHeight: '18px',
  color: active ? '#4f9a04' : theme.palette.textTertiary.main,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));

const HeadIcons = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 16,
  color: theme.palette.grey[200],
  [theme.breakpoints.down('sm')]: { gap: 12, '& svg:not(:last-of-type)': { display: 'none' } }
}));

const Thread = styled('div')(({ theme }) => ({
  padding: '18px 24px 22px',
  backgroundColor: '#f6f7f3',
  backgroundImage: 'radial-gradient(rgba(121, 201, 18, 0.10) 1px, transparent 1px)',
  backgroundSize: '18px 18px',
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: { padding: '14px 12px 16px' }
}));

const DayChip = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  padding: '3px 12px',
  borderRadius: 100,
  backgroundColor: 'rgba(255,255,255,0.9)',
  border: `1px solid ${theme.palette.grey[100]}`,
  fontFamily: 'Rubik-Regular',
  fontSize: 12,
  color: theme.palette.textTertiary.main,
  marginBottom: 14
}));

const Row = styled('div')(({ mine, tight }) => ({
  display: 'flex',
  gap: 8,
  alignItems: 'flex-end',
  justifyContent: mine ? 'flex-end' : 'flex-start',
  marginTop: tight ? 3 : 12
}));

const AvatarSlot = styled('div')(() => ({ width: 34, minWidth: 34 }));

const BubbleBox = styled('div')(({ theme, mine, tail, animate }) => ({
  position: 'relative',
  maxWidth: 'min(560px, 82%)',
  padding: '8px 12px 6px',
  borderRadius: 18,
  borderBottomLeftRadius: !mine && tail ? 5 : 18,
  borderBottomRightRadius: mine && tail ? 5 : 18,
  backgroundColor: mine ? '#e3f9b8' : '#fff',
  boxShadow: '0 1px 2px rgba(34, 34, 34, 0.08)',
  transformOrigin: mine ? '100% 100%' : '0 100%',
  ...(animate ? motion(`${bubbleIn} 260ms ease-out both`) : {}),
  [theme.breakpoints.down('sm')]: { maxWidth: '88%' }
}));

const Sender = styled('div')(({ color }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '18px',
  color,
  marginBottom: 1,
  '& span': { color: '#828282', fontWeight: 400 }
}));

const Text = styled('div')(({ theme }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  lineHeight: '22px',
  color: theme.palette.textPrimary.main,
  overflowWrap: 'anywhere'
}));

// Floats right so short messages keep the time on the same line.
const Time = styled('span')(({ read }) => ({
  float: 'right',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  margin: '6px 0 -2px 10px',
  fontFamily: 'Rubik-Regular',
  fontSize: 11,
  lineHeight: '14px',
  color: '#9aa093',
  '& svg': { color: read ? '#4f9a04' : '#9aa093', transition: 'color 300ms ease' }
}));

const Reaction = styled('span')(() => ({
  position: 'absolute',
  left: -10,
  bottom: -12,
  padding: '1px 6px',
  borderRadius: 100,
  backgroundColor: '#fff',
  boxShadow: '0 1px 3px rgba(34,34,34,0.18)',
  fontSize: 14,
  lineHeight: '20px',
  ...motion(`${popIn} 320ms ease-out both`)
}));

const Dots = styled('span')(() => ({
  display: 'inline-flex',
  gap: 4,
  padding: '6px 2px',
  '& i': {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: '#a7ad9f',
    ...motion(`${typingDot} 1.1s ease-in-out infinite`)
  },
  '& i:nth-of-type(2)': { animationDelay: '0.15s' },
  '& i:nth-of-type(3)': { animationDelay: '0.3s' }
}));

const Composer = styled('div')(({ theme }) => ({
  padding: '18px 20px 16px',
  borderTop: `1px solid ${theme.palette.grey[100]}`,
  backgroundColor: '#fff',
  [theme.breakpoints.down('sm')]: { padding: '14px 12px' }
}));

const Prompt = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  fontWeight: 500,
  color: theme.palette.textPrimary.main,
  marginBottom: 12,
  ...motion(`${fadeIn} 300ms ease-out both`),
  '& svg': { color: '#4f9a04' }
}));

const InputBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginTop: 10,
  padding: '8px 8px 8px 16px',
  borderRadius: 100,
  backgroundColor: theme.palette.grey[50],
  color: theme.palette.grey[200],
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  '& > span': {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));

const SendButton = styled('div')(({ theme }) => ({
  width: 34,
  height: 34,
  minWidth: 34,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.salad,
  color: theme.palette.textPrimary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const typingTime = (text) => Math.min(1500, Math.max(650, 380 + text.length * 9));

/* ---------- messenger ---------- */

function Messenger({ step, pack, answered, onSubmit, kpiLabels }) {
  const people = useMemo(
    () => Object.fromEntries(pack.people.map((p) => [p.key, p])),
    [pack.people]
  );
  const order = useMemo(
    () => shuffledOrder(step.options.length, seedFrom(pack.id + step.id)),
    [pack.id, step]
  );
  const messages = step.messages;
  const instant = useRef(!!answered || prefersReducedMotion()).current;
  const [shown, setShown] = useState(instant ? messages.length : 0);
  const [typing, setTyping] = useState(null); // people.key while "typing"
  const [read, setRead] = useState(!!answered);
  const [reacted, setReacted] = useState(!!answered);
  const done = shown >= messages.length;

  // Reveal the thread one message at a time with a typing indicator.
  useEffect(() => {
    if (done) {
      setTyping(null);
      return undefined;
    }
    const m = messages[shown];
    const gap = setTimeout(() => setTyping(m.from), shown === 0 ? 250 : 380);
    const show = setTimeout(
      () => {
        setTyping(null);
        setShown((n) => n + 1);
      },
      (shown === 0 ? 250 : 380) + typingTime(L(m.text))
    );
    return () => {
      clearTimeout(gap);
      clearTimeout(show);
    };
  }, [shown, done, messages]);

  // After you reply: ticks turn "read", then the recipient reacts.
  useEffect(() => {
    if (!answered || reacted) return undefined;
    const a = setTimeout(() => setRead(true), 700);
    const b = setTimeout(() => setReacted(true), 1500);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, [answered, reacted]);

  const pick = (idx) => onSubmit(optionResult(step.options[idx], idx));

  const speakers = [...new Set(messages.map((m) => m.from))].map((k) => people[k]).filter(Boolean);
  const single = speakers.length === 1;
  const FormatIcon = FORMAT_ICONS[pack.format];
  const lastSpeaker = messages[messages.length - 1]?.from;
  const picked = answered ? step.options[answered.pick] : null;

  let sub = single
    ? `${L(speakers[0].role)} · ${t('online')}`
    : speakers.map((p) => L(p.name)).join(', ');
  if (typing) sub = t('typing', { name: L(people[typing]?.name) });

  return (
    <Window>
      <Head>
        <Stack>
          {speakers.slice(0, 3).map((p, i) => (
            <Avatar key={p.key} person={p} size={40} ring={i > 0} online={single} />
          ))}
        </Stack>
        <HeadText>
          <HeadTitle>{single ? L(speakers[0].name) : `${t('formats')[pack.format]}`}</HeadTitle>
          <HeadSub active={!!typing}>{sub}</HeadSub>
        </HeadText>
        <HeadIcons aria-hidden="true">
          <Phone size={19} />
          <Video size={20} />
          <EllipsisVertical size={19} />
        </HeadIcons>
      </Head>

      <Thread
        onClick={() => !done && setShown(messages.length)}
        aria-live="polite"
        style={{ cursor: done ? 'default' : 'pointer' }}>
        <DayChip>
          {FormatIcon && <FormatIcon size={12} style={{ verticalAlign: -2, marginRight: 4 }} />}
          {t('today')} · {step.at}
        </DayChip>
        {messages.slice(0, shown).map((m, i) => {
          const p = people[m.from];
          const prev = messages[i - 1];
          const nextVisible = i + 1 < shown ? messages[i + 1] : typing ? { from: typing } : null;
          const firstOfGroup = !prev || prev.from !== m.from;
          const lastOfGroup = !nextVisible || nextVisible.from !== m.from;
          return (
            <Row key={i} tight={!firstOfGroup}>
              <AvatarSlot>{lastOfGroup && <Avatar person={p} size={34} />}</AvatarSlot>
              <BubbleBox tail={lastOfGroup} animate={!instant}>
                {firstOfGroup && !single && (
                  <Sender color={p?.color}>
                    {L(p?.name)} <span>· {L(p?.role)}</span>
                  </Sender>
                )}
                <Text>
                  {L(m.text)}
                  <Time>{addMinutes(step.at, i)}</Time>
                </Text>
              </BubbleBox>
            </Row>
          );
        })}
        {typing && (
          <Row tight={messages[shown - 1]?.from === typing}>
            <AvatarSlot>
              <Avatar person={people[typing]} size={34} />
            </AvatarSlot>
            <BubbleBox tail animate>
              <Dots aria-label={t('typing', { name: L(people[typing]?.name) })}>
                <i />
                <i />
                <i />
              </Dots>
            </BubbleBox>
          </Row>
        )}
        {picked && (
          <Row mine style={{ marginTop: 16 }}>
            <BubbleBox mine tail animate={!instant}>
              <Text>
                {L(picked.text)}
                <Time read={read}>
                  {addMinutes(step.at, messages.length + 1)}
                  <CheckCheck size={14} />
                </Time>
              </Text>
              {reacted && people[lastSpeaker] && (
                <Reaction title={L(people[lastSpeaker].name)}>{REACTION[picked.q]}</Reaction>
              )}
            </BubbleBox>
          </Row>
        )}
      </Thread>

      <Composer>
        {!answered && done && (
          <>
            {step.prompt && (
              <Prompt>
                <MessageCircleQuestion size={18} /> {L(step.prompt)}
              </Prompt>
            )}
            <ReplyOptions
              options={step.options}
              order={order}
              onPick={pick}
              icon={SendHorizontal}
            />
          </>
        )}
        {answered ? (
          <OptionFeedback
            options={step.options}
            picked={answered.pick}
            dilemma={!!step.dilemma}
            kpiLabels={kpiLabels}
            principle={step.principle}
            delay={instant ? 0 : 500}
          />
        ) : (
          <InputBar aria-hidden="true">
            <Paperclip size={18} />
            <span>{t('composerHint')}</span>
            <Smile size={18} />
            <SendButton>
              <SendHorizontal size={16} />
            </SendButton>
          </InputBar>
        )}
      </Composer>
    </Window>
  );
}

/* ---------- situation (prose) ---------- */

const SituationHead = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 16,
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  color: theme.palette.textTertiary.main,
  '& b': { color: theme.palette.textPrimary.main, fontWeight: 500 }
}));

const SituationIcon = styled('span')(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: 10,
  backgroundColor: theme.palette.secondary.light,
  color: '#b8960f',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const Scene = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: '16px 18px',
  marginBottom: 20,
  borderRadius: 12,
  backgroundColor: theme.palette.secondary.pale,
  border: `1px solid ${theme.palette.secondary.light}`,
  ...motion(`${fadeUp} 380ms ease-out 80ms both`)
}));

function Situation({ step, pack, answered, onSubmit, kpiLabels }) {
  const order = useMemo(
    () => shuffledOrder(step.options.length, seedFrom(pack.id + step.id)),
    [pack.id, step]
  );
  return (
    <Card>
      <SituationHead>
        <SituationIcon>
          <FileText size={18} />
        </SituationIcon>
        <span>
          <b>{t('situation')}</b> · {step.at}
        </span>
      </SituationHead>
      <Scene>
        <Body style={{ fontSize: 17, lineHeight: '26px' }}>{L(step.text)}</Body>
      </Scene>
      {step.prompt && <Body style={{ fontWeight: 500, marginBottom: 12 }}>{L(step.prompt)}</Body>}
      <OptionList
        options={step.options}
        order={order}
        picked={answered ? answered.pick : null}
        dilemma={!!step.dilemma}
        onPick={(idx) => onSubmit(optionResult(step.options[idx], idx))}
        kpiLabels={kpiLabels}
        principle={step.principle}
      />
    </Card>
  );
}
